import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Music, Headphones, Radio, Disc3, Music2, Music4 } from 'lucide-react';

interface SpotifyLinkingModalProps {
  isOpen: boolean;
  onRedirect: () => void;
}

const musicMessages = [
  { text: "🎵 Connexion aux serveurs de Spotify...", icon: Music },
  { text: "🎧 Préparation de vos playlists préférées...", icon: Headphones },
  { text: "📻 Syntonisation des fréquences musicales...", icon: Radio },
  { text: "💿 Chargement de vos albums favoris...", icon: Disc3 },
  { text: "🎸 Accordage des instruments...", icon: Music2 },
  { text: "🎹 Vérification des notes de musique...", icon: Music4 },
  { text: "🎤 Test du micro... 1, 2, 1, 2...", icon: Music },
  { text: "🎼 Lecture de la partition secrète...", icon: Music2 },
  { text: "🥁 Battement du rythme en cours...", icon: Headphones },
  { text: "🎺 Réveil des cuivres endormis...", icon: Radio },
];

const funFacts = [
  "Le saviez-vous ? Spotify a plus de 100 millions de chansons !",
  "Astuce : Plus vous jouez, plus vous débloquez de badges !",
  "Fun fact : La musique peut améliorer votre humeur de 25% !",
  "Le saviez-vous ? Le blindtest existe depuis les années 50 !",
  "Astuce : Liez Spotify pour jouer avec vos propres playlists !",
];

export default function SpotifyLinkingModal({ isOpen, onRedirect }: SpotifyLinkingModalProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [funFact] = useState(() => funFacts[Math.floor(Math.random() * funFacts.length)]);

  useEffect(() => {
    if (!isOpen) return;

    // Changer le message toutes les 800ms
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % musicMessages.length);
    }, 800);

    // Progression de la barre
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + 2;
      });
    }, 50);

    // Rediriger après 2.5 secondes
    const redirectTimeout = setTimeout(() => {
      onRedirect();
    }, 2500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
      clearTimeout(redirectTimeout);
    };
  }, [isOpen, onRedirect]);

  const currentMessage = musicMessages[currentMessageIndex];
  const IconComponent = currentMessage.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-dark-800 rounded-2xl p-8 max-w-md w-full mx-4 border border-dark-700 shadow-2xl"
          >
            {/* Header avec logo Spotify animé */}
            <div className="flex flex-col items-center mb-6">
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-20 h-20 rounded-full bg-[#1DB954] flex items-center justify-center mb-4 shadow-lg shadow-[#1DB954]/30"
              >
                <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </motion.div>
              <h2 className="text-xl font-display font-bold text-white">
                Connexion à Spotify
              </h2>
            </div>

            {/* Message animé avec icône */}
            <motion.div
              key={currentMessageIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex items-center gap-3 bg-dark-700/50 rounded-xl p-4 mb-6"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-10 h-10 rounded-full bg-[#1DB954]/20 flex items-center justify-center flex-shrink-0"
              >
                <IconComponent className="w-5 h-5 text-[#1DB954]" />
              </motion.div>
              <p className="text-dark-200 text-sm font-medium">
                {currentMessage.text}
              </p>
            </motion.div>

            {/* Barre de progression */}
            <div className="mb-6">
              <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#1DB954] to-[#1ed760] rounded-full"
                  initial={{ width: "0%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.1 }}
                />
              </div>
              <p className="text-dark-400 text-xs mt-2 text-center">
                {progress < 100 ? "Préparation de la connexion..." : "Redirection en cours..."}
              </p>
            </div>

            {/* Notes de musique animées */}
            <div className="flex justify-center gap-2 mb-6">
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [0, -10, 0],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: i * 0.1,
                  }}
                  className="text-[#1DB954] text-2xl"
                >
                  ♪
                </motion.div>
              ))}
            </div>

            {/* Fun fact */}
            <div className="text-center">
              <p className="text-dark-400 text-xs italic">
                💡 {funFact}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
